import { StyledEngineProvider } from '@mui/material/styles';
import { Component, lazy, Suspense } from 'react';
import './App.css';
import { ImgWithFallback } from './controls/ImgWithFallback';
import FiveThreeOneForm from './Form';
import Persistence from './Persistence';
import PlateCalculator from './PlateCalculator';
import generateProgram from './Program';
import igLogoPng from './resources/ig.png';
import igLogo from './resources/ig.webp';
import logo from './resources/logo.svg';
import { Templates } from './TemplatePicker';
import { WeekDayType } from './TemplateSchedule';
import Units from './Units';
const Upsell = lazy(() => import('./Upsell'));
const ProgramTable = lazy(() => import('./ProgramTable'));


// The keys are put as string as you can't have a "." in a object litteral key.
const defaultLbsPlating = {
  "100": 0,
  "55": 0,
  "45": 10,
  "35": 0,
  "25": 1,
  "15": 0,
  "10": 2,
  "5": 1,
  "2.5": 1,
  "1.25": 0,
  "1": 0,
  "0.75": 0,
  "0.5": 0,
  "0.25": 0,
};

const defaultKgsPlating = {
  "50": 0,
  "25": 0,
  "20": 10,
  "15": 0,
  "10": 1,
  "5": 1,
  "2.5": 1,
  "2": 0,
  "1.5": 0,
  "1": 0,
  "1.25": 1,
  "0.75": 0,
  "0.5": 0,
  "0.25": 0,
  "0.125": 0,
};

const defaultFormState = {
  unit: Units.Lbs,
  trainingMaxes: [67.5, 90, 135, 180],
  schedule: {
    daysAWeek: 4,
    liftsOrder: [WeekDayType.overheadPress, WeekDayType.deadlift, WeekDayType.benchPress, WeekDayType.squat],
    bastardOrder: false,
    threeFiveOne: false,
  },
  additionalOptions: {
    warmup: 0,
    upperBase: 95,
    lowerBase: 135,
    jokerMax: 0,
    deload: 0,
    deloadSkipWarmup: true,
  },
  plating: {
    lbsPlates: defaultLbsPlating,
    lbsBarbellWeight: 45.0,
    kgsPlates: defaultKgsPlating,
    kgsBarbellWeight: 20.0,
  },
  template: {
    pick: Templates.BoringButBig,
    boringButBigOptions: {
      option: 0,
      ratios: [30, 30, 30, 30],
      deload: 0,
    },
    bodyweightOptions: {
      repCount: 75,
      setCount: 5,
    },
    simplestStrengthOptions: {
      trainingMaxes: [93, 116.25, 139.5, 162.75],
    },
    beginnersOptions: {
      intermediate: false,
    },
    fullBodyOptions: {
      phase: 0,
      option: 0,
      ratios: [0, 0, 0],
    },
    twoDayAWeekOptions: {
      option: 0,
      percentageIndex: 0,
    },
    firstSetLastOptions: {
      option: 0,
      setCount: 3,
      repCount: 5,
    },
    gvtOptions: {
      lessBoring: false,
      ratios: [30, 30, 30, 30],
    },
    bbbChallengeOptions: {
      option: 0,
      lessBoring: true,
    }
  },
  output: {
    programTitle: "",
    printQR: true,
    computePlating: true,
  },
};

const upsellDismissedKey = "upsell-dismissed"

function barbellWeight(form) {
  if (form.unit === Units.Kg) {
    return form.plating.kgsBarbellWeight;
  } else {
    return form.plating.lbsBarbellWeight;
  }
}

function plateMapping(form) {
  if (form.unit === Units.Kg) {
    return form.plating.kgsPlates;
  } else {
    return form.plating.lbsPlates;
  }
}

function urlGetter(formState) {
  let url;
  return function () {
    if (url) {
      return url;
    }

    url = Persistence.generateUrl(formState);
    return url;
  }
}

const programStorageKey = "program";
const formStorageKey = "form";
class App extends Component {
  constructor(props) {
    super(props);

    const shouldShow = !Boolean(window.localStorage.getItem(upsellDismissedKey));

    const storedFormState = Persistence.getItem(formStorageKey);

    let deepMerges;
    if (storedFormState != null) {
      // Stored form might not have a full template or plating if coming from URL.
      // Therefore we need to merge these manually.
      deepMerges = {
        plating: Object.assign({ ...defaultFormState.plating }, storedFormState.plating),
        template: Object.assign({ ...defaultFormState.template }, storedFormState.template),
        output: Object.assign({ ...defaultFormState.output }, storedFormState.output),
      };
    }

    const initialFormState = Object.assign({ ...defaultFormState }, storedFormState, deepMerges);

    this.plateCalculator = new PlateCalculator(
      barbellWeight(initialFormState),
      plateMapping(initialFormState));

    let program = Persistence.getItem(programStorageKey);

    // Make sure the URL is expected. The encoding has changed in version 1.1 so this just
    // re-encodes the url to the new method.
    if (program && program.url !== Persistence.generateUrl(initialFormState)) {
      program.url = Persistence.generateUrl(initialFormState);
    }

    // To handle preiously saved programs that don't have the "new" way of doing things.
    if (program && !program.urlGetter && program.url) {
      program.urlGetter = function () { return program.url; };
    }

    if (!program) {
      program = generateProgram(
        initialFormState.trainingMaxes,
        initialFormState.template,
        initialFormState.additionalOptions,
        this.plateCalculator,
        initialFormState.schedule,
        initialFormState.output.programTitle,
        initialFormState.unit,
        urlGetter(initialFormState));
    }
    this.state = {
      program: program,
      form: initialFormState,
      showUpsell: shouldShow,
    };
  }

  onFormChanged = (newForm) => {
    Persistence.setItem(formStorageKey, newForm);
    this.plateCalculator = new PlateCalculator(
      barbellWeight(newForm), plateMapping(newForm));

    const newUrl = Persistence.generateUrl(newForm);
    const newProgram = generateProgram(
      newForm.trainingMaxes,
      newForm.template,
      newForm.additionalOptions,
      this.plateCalculator,
      newForm.schedule,
      newForm.output.programTitle,
      newForm.unit,
      function () { return newUrl; },
    );

    window.history.replaceState({}, "", newUrl);

    Persistence.setItem(programStorageKey, newProgram);
    this.setState({ program: newProgram, form: newForm });
  }

  dismissUpsell = () => {
    window.localStorage.setItem(upsellDismissedKey, "twas");
    this.setState({ showUpsell: false });
  }

  render() {
    let upsell;
    if (this.state.showUpsell) {
      upsell = <Suspense fallback={<div></div>}>
        <Upsell onDismiss={this.dismissUpsell} />
      </Suspense>;
    }

    return (
      <StyledEngineProvider injectFirst>
        <div className="App">
          <header>
            <a href="/"><img src={logo} className="logo" alt="Homepage" /></a>
            <h1>5/3/1 Calculator</h1>
          </header>
          <p className="no-print bottom-pad-8"><strong>November 2024 Update: Small fixes</strong></p>
          <p className="no-print">Jim Wendler's 5/3/1 program is famous because it's simple and effective. This calculator is purposefully light on explanations and assumes that you are already familiar with 5/3/1. If that isn't the case, we recommend you <a href="https://fivethreeone.app#buy-the-books">buy the books</a>.</p>
          <FiveThreeOneForm onFormChanged={this.onFormChanged} formData={this.state.form} plateCalculator={this.plateCalculator} />
          {upsell}
          <Suspense fallback={<div></div>}>
            <ProgramTable program={this.state.program} options={this.state.form.output} />
          </Suspense>
          <footer>
            <div className="no-print">
              <p className="bottom-pad-8">Version 2.2 - Last updated November 20 2024</p>
              <div className="bottom-links">
                <a href="https://www.instagram.com/fivethreeoneapp/"><ImgWithFallback src={igLogo} fallback={igLogoPng} alt="Instagram Page" width="32"
                  height="32" loading="lazy" /></a>
                <p className="support"><a href="/support">Support & Privacy</a></p>
              </div>
              <p>Made with 💪 in Seattle</p>
              <p>By <a href="https://strongpigeon.dev">Strong Pigeon LLC</a></p>
            </div>
            <div className="print-only">
              <img src={logo} className="logo" alt="Homepage" />
              <div>
                <h3>Made using 5/3/1 Calculator</h3>
                <p>By Strong Pigeon LLC</p>
              </div>
            </div>
          </footer>
        </div>
      </StyledEngineProvider>
    );
  }
}

export default App;
